import type { ReactNode } from 'react';
import { Input } from '@london-life/ui/components/input';
import { Label } from '@london-life/ui/components/label';

// The content editors' shared form pieces: a labelled field, numbers typed in (pounds kept as pence, shares
// shown as percentages), limits, and ids made from names. One place, so every editor reads and saves numbers
// the same way.

// a typed number, or the fallback when the box is empty or not a number
export const number = (text: string, fallback: number) =>
  text.trim() === '' || !Number.isFinite(Number(text)) ? fallback : Number(text);
// a whole number
export const wholeNumber = (text: string, fallback: number) => Math.round(number(text, fallback));
// pounds typed in, kept as pence (never below nothing)
export const toPence = (text: string, fallback: number) => Math.max(0, Math.round(number(text, fallback / 100) * 100));
export const within = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

// a new item's id from its name ("Bao buns" → "bao_buns"), kept unique among those taken
export function idFor(label: string, taken: Set<string>, fallback: string, length = 26) {
  const base =
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, length) || fallback;
  let id = base;
  for (let n = 2; taken.has(id); n += 1) id = `${base}_${n}`;
  return id;
}

export function Field({ id, label, children }: { id: string; label: string; children: ReactNode }) {
  return (
    <div className="admin-field">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}

// a number box described once: its label, how it is typed (pounds kept in pence, a share shown as a
// percentage, or a plain number), its limits, and whether it is whole
// (basis points, as rates are kept: 50 is 0.5%)
export type Unit = 'pounds' | 'percent' | 'bps' | 'number';
export type NumberSpec<K extends string = string> = {
  key: K;
  label: string;
  unit: Unit;
  min: number;
  max: number;
  whole?: boolean;
};
// what something does to one need, -50 to +50 (a date's fun, a feed's social)
export const needSpec = <K extends string>(key: K, label: string): NumberSpec<K> => ({
  key,
  label,
  unit: 'number',
  min: -50,
  max: 50,
  whole: true,
});
const shown = (value: number, unit: Unit) =>
  unit === 'pounds' || unit === 'bps' ? value / 100 : unit === 'percent' ? Math.round(value * 10_000) / 100 : value;
const stored = (text: string, current: number, spec: NumberSpec) => {
  const typed = number(text, shown(current, spec.unit));
  const value =
    spec.unit === 'pounds' || spec.unit === 'bps'
      ? Math.round(typed * 100)
      : spec.unit === 'percent'
        ? typed / 100
        : typed;
  return within(
    spec.whole || spec.unit === 'pounds' || spec.unit === 'bps' ? Math.round(value) : value,
    spec.min,
    spec.max,
  );
};
const suffix = (unit: Unit) => (unit === 'pounds' ? ' (£)' : unit === 'percent' || unit === 'bps' ? ' (%)' : '');

export function NumberField({
  id,
  spec,
  value,
  onChange,
}: {
  id: string;
  spec: NumberSpec;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <Field id={id} label={spec.label + suffix(spec.unit)}>
      <Input
        id={id}
        type="number"
        step="any"
        value={shown(value, spec.unit)}
        onChange={(e) => onChange(stored(e.target.value, value, spec))}
      />
    </Field>
  );
}

// a place on the map chosen from the content's places (one the content names but the map lacks is kept, marked)
export function PlaceSelect({
  id,
  label,
  value,
  places,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  places: { id: string; name: string }[];
  onChange: (placeId: string) => void;
}) {
  return (
    <Field id={id} label={label}>
      <select id={id} className="content-select" value={value} onChange={(e) => onChange(e.target.value)}>
        {!places.some((place) => place.id === value) && <option value={value}>{value} (not on the map)</option>}
        {places.map((place) => (
          <option key={place.id} value={place.id}>
            {place.name}
          </option>
        ))}
      </select>
    </Field>
  );
}
